import { Link, useNavigate } from '@tanstack/react-router';
import { FolderKanban, PlayCircle, Plus } from 'lucide-react';
import { useState } from 'react';
import { PageHeader } from '../../components/layout/PageHeader';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  EntityKey,
  ErrorState,
  ProgressBar,
  RunStatusBadge,
  SkeletonRows,
  Stat,
  Table,
  TableWrap,
  Td,
  Th,
  Tr,
} from '../../components/ui';
import { useFormat, useT } from '../../i18n';
import { useCurrentUser } from '../auth/api';
import { usePermissions } from '../auth/permissions';
import { ProjectFormDialog } from '../projects/ProjectFormDialog';
import { useDashboard } from './api';

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const { can } = usePermissions();
  const { data, isPending, error, refetch } = useDashboard();
  const [createOpen, setCreateOpen] = useState(false);
  const { t } = useT();
  const { relative } = useFormat();

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <>
      <PageHeader
        title={t('dashboard.welcome', { name: user?.name.split(' ')[0] ?? '' })}
        description={t('dashboard.subtitle')}
        actions={
          can('project:write') ? (
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              {t('projects.new')}
            </Button>
          ) : null
        }
      />

      <ProjectFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(project) =>
          void navigate({
            to: '/projects/$projectCode/overview',
            params: { projectCode: project.code },
          })
        }
      />

      {isPending || !data ? (
        <SkeletonRows rows={4} className="p-0" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Stat
              label={t('dashboard.statProjects')}
              value={data.projectCount}
              hint={t('dashboard.statProjectsHint', { count: data.activeProjectCount })}
            />
            <Stat label={t('dashboard.statTestCases')} value={data.testCaseCount} />
            <Stat label={t('dashboard.statChecklists')} value={data.checklistCount} />
            <Stat label={t('dashboard.statTestRuns')} value={data.testRunCount} />
            <Stat
              label={t('dashboard.statActiveRuns')}
              value={data.activeRuns.length}
              valueClassName={data.activeRuns.length > 0 ? 'text-accent-fg' : undefined}
            />
          </div>

          <Card>
            <CardHeader
              title={t('dashboard.activeRuns')}
              description={t('dashboard.activeRunsHint')}
              action={
                <Link
                  to="/projects"
                  className="text-[13px] font-medium text-accent-fg hover:underline"
                >
                  {t('dashboard.allProjects')}
                </Link>
              }
            />
            {data.activeRuns.length === 0 ? (
              <EmptyState
                icon={<PlayCircle className="size-6" />}
                title={t('dashboard.noActiveRuns')}
                description={t('dashboard.noActiveRunsHint')}
              />
            ) : (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th className="w-full">{t('dashboard.columnRun')}</Th>
                      <Th>{t('dashboard.columnEnvironment')}</Th>
                      <Th>{t('dashboard.columnAssignee')}</Th>
                      <Th className="w-48">{t('common.progress')}</Th>
                      <Th>{t('common.updated')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.activeRuns.map((run) => (
                      <Tr key={run.id}>
                        <Td>
                          <Link
                            to="/projects/$projectCode/test-runs/$runKey"
                            params={{ projectCode: run.runKey.split('-')[0] ?? '', runKey: run.runKey }}
                            className="font-medium text-ink hover:text-accent-fg"
                          >
                            {run.title}
                          </Link>
                          <div className="mt-0.5">
                            <EntityKey>{run.runKey}</EntityKey>
                          </div>
                        </Td>
                        <Td>{run.environment?.name ?? t('common.empty')}</Td>
                        <Td>{run.assignee?.name ?? t('common.unassigned')}</Td>
                        <Td>
                          <div className="flex items-center gap-2">
                            <ProgressBar value={run.stats.progress} className="w-28" />
                            <span className="text-[12px] text-ink-muted">
                              {run.stats.executed}/{run.stats.total}
                            </span>
                          </div>
                        </Td>
                        <Td className="text-ink-muted">{relative(run.updatedAt)}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader title={t('dashboard.latestRuns')} />
              {data.recentRuns.length === 0 ? (
                <EmptyState title={t('dashboard.noRuns')} />
              ) : (
                <ul className="divide-y divide-border">
                  {data.recentRuns.map((run) => (
                    <li key={run.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                      <div className="min-w-0">
                        <Link
                          to="/projects/$projectCode/test-runs/$runKey"
                          params={{ projectCode: run.runKey.split('-')[0] ?? '', runKey: run.runKey }}
                          className="block truncate text-[13px] font-medium text-ink hover:text-accent-fg"
                        >
                          {run.title}
                        </Link>
                        <p className="mt-0.5 text-[12px] text-ink-subtle">
                          <EntityKey>{run.runKey}</EntityKey> · {relative(run.createdAt)}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <span className="text-[12px] text-pass">
                          {t('dashboard.passedShort', { count: run.stats.passed })}
                        </span>
                        <span className="text-[12px] text-fail">
                          {t('dashboard.failedShort', { count: run.stats.failed })}
                        </span>
                        <RunStatusBadge status={run.status} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card>
              <CardHeader title={t('dashboard.recentCases')} />
              {data.recentCases.length === 0 ? (
                <EmptyState
                  icon={<FolderKanban className="size-6" />}
                  title={t('dashboard.noCases')}
                  description={t('dashboard.noCasesHint')}
                />
              ) : (
                <ul className="divide-y divide-border">
                  {data.recentCases.map((testCase) => (
                    <li key={testCase.id} className="px-4 py-2.5">
                      <Link
                        to="/projects/$projectCode/test-cases/$caseKey"
                        params={{ projectCode: testCase.projectCode, caseKey: testCase.caseKey }}
                        className="block truncate text-[13px] font-medium text-ink hover:text-accent-fg"
                      >
                        {testCase.title}
                      </Link>
                      <p className="mt-0.5 text-[12px] text-ink-subtle">
                        <EntityKey>{testCase.caseKey}</EntityKey> ·{' '}
                        {testCase.folderName ?? t('dashboard.noFolder')} ·{' '}
                        {relative(testCase.createdAt)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </>
      )}
    </>
  );
};
